"use client"

import { useState, useEffect } from "react"
import { createPortal } from "react-dom"
import { Check, X, Users } from "lucide-react"
import { Z } from "@/lib/z-index"
import { useAnimatedClose } from "@/lib/use-animated-close"
import type { CommunityPost } from "@/lib/community/types"
import { ABILITY_TYPE_CONFIG } from "@/lib/community/types"

interface DeployTeamPanelProps {
  post: CommunityPost
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
}

const teamConfig = ABILITY_TYPE_CONFIG["agent-team"]

/**
 * DeployTeamPanel — centered glass modal for agent team deployment confirmation.
 * Shows team composition and confirms deploy action.
 */
export function DeployTeamPanel({
  post,
  isOpen,
  onClose,
  onConfirm,
}: DeployTeamPanelProps) {
  const { closing, requestClose } = useAnimatedClose(onClose, 280)
  const [entered, setEntered] = useState(false)
  const [deploying, setDeploying] = useState(false)
  const [deployed, setDeployed] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setEntered(false)
      const raf = requestAnimationFrame(() => setEntered(true))
      return () => cancelAnimationFrame(raf)
    }
  }, [isOpen])

  const handleConfirm = async () => {
    if (deploying || deployed) return
    setDeploying(true)
    try {
      await onConfirm()
    } finally {
      setDeploying(false)
      setDeployed(true)
      setTimeout(() => {
        setDeployed(false)
        requestClose()
      }, 1500)
    }
  }

  const panelStyle = {
    opacity: closing ? 0 : entered ? 1 : 0,
    transform: closing
      ? "scale(0.96) translateY(8px)"
      : entered
        ? "scale(1) translateY(0)"
        : "scale(0.92) translateY(12px)",
    filter: closing ? "blur(4px)" : entered ? "blur(0)" : "blur(6px)",
    transition: "opacity 280ms, transform 280ms, filter 280ms",
    transitionTimingFunction: closing
      ? "cubic-bezier(0.4, 0, 1, 1)"
      : "cubic-bezier(0.16, 1, 0.3, 1)",
  }

  if (!isOpen) return null

  const members = post.teamMembers ?? []

  return createPortal(
    <>
      {/* Overlay backdrop */}
      <div
        className={`liquid-glass-overlay fixed inset-0 transition-all duration-300 ease-out ${
          closing
            ? "opacity-0 backdrop-blur-none"
            : entered
              ? "opacity-100 backdrop-blur-xl"
              : "opacity-0 backdrop-blur-none"
        }`}
        style={{ zIndex: Z.MODAL_NESTED, pointerEvents: closing || !entered ? "none" : "auto" }}
        onClick={requestClose}
        aria-hidden
      />

      {/* Centered modal container */}
      <div
        className="fixed inset-0 flex items-center justify-center p-4"
        style={{ zIndex: Z.MODAL_NESTED + 1, pointerEvents: "none" }}
      >
        <div
          className="w-full max-w-md"
          style={{ ...panelStyle, pointerEvents: "auto" }}
        >
          <div className="liquid-glass-modal rounded-2xl flex flex-col">
            {/* Header with gradient accent */}
            <div
              className="h-1 w-full rounded-t-2xl"
              style={{
                background: `linear-gradient(90deg, ${teamConfig.hex}, ${teamConfig.hexEnd ?? teamConfig.hex})`,
              }}
            />
            <div className="flex items-center justify-between px-6 py-4 border-b border-border shrink-0">
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-red-500" />
                <h2 className="text-base font-semibold text-foreground">
                  获取智能体团队
                </h2>
              </div>
              <button
                onClick={requestClose}
                className="flex h-7 w-7 items-center justify-center rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>

            {/* Content */}
            <div className="px-6 py-5">
              {deployed ? (
                <div className="flex flex-col items-center justify-center py-6 gap-3">
                  <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary/20 text-primary">
                    <Check size={28} strokeWidth={2.5} />
                  </div>
                  <p className="text-base font-medium text-primary">
                    团队已部署到你的工作空间！
                  </p>
                </div>
              ) : (
                <>
                  {/* Team name */}
                  <p className="text-sm font-medium text-foreground mb-3">
                    {post.title}
                  </p>

                  {/* Team members */}
                  {members.length > 0 && (
                    <div className="liquid-glass-surface rounded-xl p-3 mb-4 space-y-2.5">
                      {members.map((member, idx) => (
                        <div key={member.name} className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8 rounded-full text-xs font-medium text-red-500"
                            style={{ background: `linear-gradient(135deg, ${teamConfig.hex}20, ${teamConfig.hexEnd ?? teamConfig.hex}20)` }}
                          >
                            {idx + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-foreground truncate">
                              {member.name}
                            </p>
                            <p className="text-caption text-muted-foreground truncate">
                              {member.role}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Capabilities */}
                  {post.capabilities && post.capabilities.length > 0 && (
                    <div className="flex flex-wrap gap-1 mb-4">
                      {post.capabilities.map((cap) => (
                        <span
                          key={cap}
                          className="text-micro px-2 py-0.5 rounded-full bg-red-500/10 text-red-500 border border-red-500/20"
                        >
                          {cap}
                        </span>
                      ))}
                    </div>
                  )}

                  <p className="text-xs text-muted-foreground text-center">
                    将{members.length}个智能体部署为协作团队到你的工作空间
                  </p>
                </>
              )}
            </div>

            {/* Footer */}
            {!deployed && (
              <div className="px-6 py-4 border-t border-border shrink-0 flex flex-col gap-2">
                <button
                  onClick={handleConfirm}
                  disabled={deploying}
                  className={[
                    "w-full h-9 flex items-center justify-center gap-2",
                    "rounded-xl text-white text-sm font-semibold",
                    "transition-opacity duration-150",
                    deploying ? "opacity-70 cursor-not-allowed" : "hover:opacity-90",
                  ].join(" ")}
                  style={{
                    background: `linear-gradient(135deg, ${teamConfig.hex}, ${teamConfig.hexEnd ?? teamConfig.hex})`,
                  }}
                >
                  {deploying ? (
                    <>
                      <span
                        className="inline-block w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin"
                        aria-hidden="true"
                      />
                      部署中…
                    </>
                  ) : (
                    "确认"
                  )}
                </button>
                <button
                  onClick={requestClose}
                  className="w-full text-center text-sm text-muted-foreground hover:text-foreground transition-colors py-1"
                >
                  取消
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>,
    document.body,
  )
}
